import React from 'react';
import { Link } from 'react-router-dom';
import {
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Sparkles
} from 'lucide-react';

const Footer = () => {
  const footerLinks = {
    shop: [
      { label: 'iPhone Cases', href: '/shop/iphone-cases' },
      { label: 'Samsung Cases', href: '/shop/samsung-cases' },
      { label: 'Accessories', href: '/shop/accessories' },
      { label: 'New Arrivals', href: '/shop/new-arrivals' },
      { label: 'Best Sellers', href: '/shop/best-sellers' },
      { label: 'Sale', href: '/shop/sale' }
    ],
    support: [
      { label: 'Contact Us', href: '/contact' },
      { label: 'FAQs', href: '/faqs' },
      { label: 'Shipping Info', href: '/shipping' },
      { label: 'Returns & Exchanges', href: '/returns' },
      { label: 'Warranty', href: '/warranty' },
      { label: 'Track Order', href: '/track' }
    ],
    company: [
      { label: 'About Amlaria', href: '/about' },
      { label: 'Our Lounge', href: '/lounge' },
      { label: 'Sustainability', href: '/sustainability' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms & Conditions', href: '/terms' }
    ]
  };

  return (
    <footer className="bg-stone-900 text-stone-300" data-testid="footer">
      {/* Kilim Pattern Top Border */}
      <div className="h-4 w-full"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, 
            #b45309 0px, #b45309 40px, 
            #dc2626 40px, #dc2626 80px, 
            #78350f 80px, #78350f 120px,
            #ea580c 120px, #ea580c 160px,
            #fbbf24 160px, #fbbf24 200px)`
        }}
      ></div>

      {/* Main Footer Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <Link to="/" className="inline-flex items-center gap-1">
                <span className="text-amber-500 text-4xl font-black">A</span>
                <span className="text-white text-3xl font-bold">mlaria</span>
              </Link>
            </div>
            
            <p className="text-stone-400 mb-6 leading-relaxed">
              Where traditional artistry meets modern protection. Premium phone covers and accessories 
              crafted with passion and inspired by bohemian luxury.
            </p>

            {/* Lounge Highlight */}
            <Link to="/lounge" className="block mb-8">
              <div className="bg-gradient-to-r from-amber-900/50 to-red-900/50 border border-amber-600/30 p-4 hover:border-amber-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="h-5 w-5 text-amber-400" />
                  <span className="text-amber-300 font-semibold">Visit Our Lounge</span>
                </div>
                <p className="text-sm text-stone-400">Experience the Amlaria showroom in person</p>
              </div>
            </Link>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" 
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 flex items-center justify-center transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 flex items-center justify-center transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 flex items-center justify-center transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 flex items-center justify-center transition-colors">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-stone-800 hover:bg-amber-700 flex items-center justify-center transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h3 className="text-amber-400 font-bold text-lg mb-6 uppercase tracking-wider">Shop</h3>
            <ul className="space-y-3">
              {footerLinks.shop.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-stone-400 hover:text-amber-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-amber-400 font-bold text-lg mb-6 uppercase tracking-wider">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-stone-400 hover:text-amber-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links + Contact */}
          <div>
            <h3 className="text-amber-400 font-bold text-lg mb-6 uppercase tracking-wider">Company</h3>
            <ul className="space-y-3 mb-8">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-stone-400 hover:text-amber-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 text-amber-500" />
                <span className="text-stone-400">+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 text-amber-500" />
                <span className="text-stone-400">hello@amlaria.com</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-stone-400">123 Fashion Avenue<br/>Design District, NY 10001</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mt-12 pt-8 border-t border-stone-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Payment Icons */}
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <span className="text-sm text-stone-500 mr-2">We Accept:</span>
              <div className="flex items-center gap-3">
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/320px-Visa_Inc._logo.svg.png" alt="Visa" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/320px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/320px-PayPal.svg.png" alt="PayPal" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/320px-American_Express_logo_%282018%29.svg.png" alt="Amex" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
              </div>
            </div>

            {/* Device Compatibility */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-stone-500">Compatible with:</span>
              <div className="flex items-center gap-3 bg-stone-800 px-4 py-2">
                <span className="text-amber-400 font-semibold text-sm">iPhone 15-16</span>
                <span className="text-stone-600">|</span>
                <span className="text-amber-400 font-semibold text-sm">Samsung S24-S25</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright with Kilim Pattern */}
      <div className="bg-stone-950">
        <div className="h-2"
          style={{
            backgroundImage: `repeating-linear-gradient(90deg, 
              #78350f 0px, #78350f 20px, 
              #dc2626 20px, #dc2626 40px, 
              #f59e0b 40px, #f59e0b 60px)`
          }}
        ></div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-xs text-stone-500">
            © {new Date().getFullYear()} Amlaria. All rights reserved. Premium Phone Covers & Accessories.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
